import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useMembershipPlans, type MembershipPlan } from '@/hooks/useMembershipPlans';
import { useEvents, type EventListItem } from '@/hooks/useEvents';
import { listSubscriptionPlans } from '@/lib/api/core';
import { Badge, Button, Card } from '@/components/ui';
import { formatCOP } from '@/lib/format';

type Cycle = 'monthly' | 'annual';

/** Identidad visual por plan (decisión de diseño por nivel de precio, NO marca
 *  "recomendado" — el backend tiene `recommended:false` en todos). */
type PlanVariant = 'free' | 'blue' | 'flagship';

/**
 * Beneficios REALES por plan (`features`/`benefits`), read-only y LOCAL a esta
 * página. El hook compartido `useMembershipPlans` deja `features:[]`/`benefits:[]`
 * (no los expone) y lo consume también el detalle `/memberships/:slug`; para no
 * tocar ese hook ni esa ruta, acá se leen del MISMO endpoint (`/subscriptions/
 * plans`, vía el cliente existente) solo para mostrarlos en las cards y la tabla.
 * No cambia datos ni contrato de API: es lectura del catálogo ya existente.
 */
function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
}

function useMembershipBenefits() {
  return useQuery({
    queryKey: ['membership-plans', 'benefits-detail'],
    queryFn: async () => {
      const data = await listSubscriptionPlans();
      const map = new Map<string, { features: string[]; benefits: string[] }>();
      for (const p of data) {
        const slug = typeof p.slug === 'string' && p.slug.trim() ? p.slug.trim() : String(p.id);
        map.set(slug, { features: toStringList(p.features), benefits: toStringList(p.benefits) });
      }
      return map;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Fondo del hero = la MISMA imagen limpia de concierto que usan los heroes del
 * Home y de Eventos: la portada de evento más repetida del catálogo real (en el
 * seed, una escena de público en concierto SIN branding de terceros). Se reusa
 * vía `useEvents` (read-only), sin duplicar el asset. Sin portadas → null.
 */
function pickConcertBackdrop(events: ReadonlyArray<EventListItem>): string | null {
  const counts = new Map<string, number>();
  for (const e of events) {
    if (e.coverImage) counts.set(e.coverImage, (counts.get(e.coverImage) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [url, n] of counts) {
    if (n > bestCount) {
      best = url;
      bestCount = n;
    }
  }
  return best;
}

/**
 * Hero de Membresías — lenguaje premium de Mi Boleta con la MISMA imagen limpia
 * de concierto/público del Home/Eventos (`heroImage`), tratada a sangre y
 * fundida con el fondo (blur/oscurecido + duotono azul→violeta→cian + scrim +
 * glows + grid). Sin branding de First Class. Encima, el SÍMBOLO oficial de Mi
 * Boleta (`/mi-boleta-mark.png`) con un glow azul discreto. Copy intacto.
 */
function MembershipsHero({ heroImage }: { heroImage: string | null }): JSX.Element {
  return (
    <section className="relative isolate flex min-h-[280px] items-center overflow-hidden bg-[#0a0f1f] text-white md:min-h-[340px]">
      {/* Backdrop — imagen real de concierto (limpia), a sangre y fundida. */}
      {heroImage && (
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          className="pointer-events-none absolute inset-0 -z-20 h-full w-full scale-105 object-cover object-[center_35%] opacity-[0.55]"
          style={{ filter: 'saturate(1.1)' }}
        />
      )}

      {/* Duotono azul→violeta→cian sobre la foto (fusión con la paleta). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(120deg, rgba(0,118,244,0.48) 0%, rgba(124,58,237,0.44) 58%, rgba(0,214,238,0.30) 100%)',
          mixBlendMode: 'overlay',
        }}
      />

      {/* Scrim de legibilidad — oscuro a la izquierda (texto) y hacia abajo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(90deg, rgba(10,15,31,0.94) 0%, rgba(10,15,31,0.80) 38%, rgba(10,15,31,0.45) 68%, rgba(10,15,31,0.58) 100%),' +
            'linear-gradient(180deg, rgba(10,15,31,0.5) 0%, transparent 30%, transparent 55%, rgba(10,15,31,0.92) 100%)',
        }}
      />

      {/* Glows radiales (azul Mi Boleta → violeta → cian). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(55% 90% at 8% 0%, rgba(0,118,244,0.40) 0%, transparent 60%),' +
            'radial-gradient(55% 90% at 98% 12%, rgba(124,58,237,0.38) 0%, transparent 62%),' +
            'radial-gradient(60% 90% at 82% 116%, rgba(0,214,238,0.18) 0%, transparent 60%)',
        }}
      />

      {/* Grid técnica muy sutil para textura. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(120% 100% at 28% 0%, #000 18%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(120% 100% at 28% 0%, #000 18%, transparent 78%)',
        }}
      />

      <div className="container-fc relative w-full py-12 md:py-16">
        <div className="max-w-2xl">
          <img
            src="/mi-boleta-mark.png"
            alt="Mi Boleta"
            className="h-14 w-auto flex-none object-contain drop-shadow-[0_6px_28px_rgba(0,118,244,0.5)] md:h-16"
          />

          <h1 className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)] md:text-4xl lg:text-5xl">
            Membresías
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/85 md:text-lg">
            Planes diseñados para acompañarte según cómo vivas First Class. Cambiá de plan cuando
            quieras; cada nivel desbloquea más beneficios, mejor servicio y experiencias más
            curadas.
          </p>
        </div>
      </div>

      {/* Borde inferior suave hacia el canvas claro del contenido. */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
      />
    </section>
  );
}

/**
 * Memberships — ROUND7 P2 / ROUND8 UAT-C04 / ROUND9 UAT-C08 / ROUND17 #4.
 * Landing con grid de planes + tabla de comparación.
 *
 * ROUND17 #4: toggle GLOBAL Mensual/Anual arriba del grid. Al cambiar el
 * toggle, las cards muestran el precio + puntos del ciclo elegido y el
 * CTA "Ver detalle" navega a `/memberships/<slug>?cycle=<cycle>` para que
 * la página de detalle abra con el ciclo prefilled.
 *
 * REDISEÑO VISUAL (solo estético, SPEC-015): cards premium con toda la info
 * dentro (badge, precio, descripción, puntos, beneficios reales, CTA) y planes
 * diferenciados por identidad (Gratuito neutro · Premium azul · First Class
 * oscuro). Tabla comparativa refinada. Cero cambios de datos/hooks/rutas ni de
 * la lógica de suscripción.
 */
export function MembershipsPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: basePlans, isLoading, isError } = useMembershipPlans();
  // Beneficios reales (local, read-only) fusionados de forma INMUTABLE en una
  // copia para mostrar en cards + tabla. No muta la data del hook compartido.
  const { data: benefitsBySlug } = useMembershipBenefits();
  // Fondo del hero = misma imagen limpia de concierto que Home/Eventos (read-only,
  // sin duplicar el asset; sin branding de terceros).
  const { data: eventsForHero } = useEvents();
  const heroBackdrop = pickConcertBackdrop(eventsForHero ?? []);
  const plans = useMemo(() => {
    if (!basePlans) return basePlans;
    if (!benefitsBySlug) return basePlans;
    return basePlans.map((p) => {
      const extra = benefitsBySlug.get(p.slug);
      return extra ? { ...p, features: extra.features, benefits: extra.benefits } : p;
    });
  }, [basePlans, benefitsBySlug]);
  // Default a `?cycle=` (mismo formato que el detalle); soporta el
  // cross-platform redirect desde web/app per ROUND17 #10.
  const initialCycle: Cycle = searchParams.get('cycle') === 'annual' ? 'annual' : 'monthly';
  const [cycle, setCycle] = useState<Cycle>(initialCycle);

  // ROUND17 #10: redirect desde web/app pueden traer `?plan_slug=premium&
  // cycle=annual` apuntando al listing. Si llega ese param y matchea un
  // plan, redirigimos al detalle con el ciclo prefilled.
  useEffect(() => {
    const planSlug = searchParams.get('plan_slug');
    if (!planSlug || !plans) return;
    const match = plans.find((p) => p.slug === planSlug);
    if (match) {
      navigate(`/memberships/${match.slug}?cycle=${initialCycle}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  // Nivel de precio más alto → identidad "flagship" (First Class). Decisión
  // visual; no altera datos ni marca "recomendado".
  const flagshipSlug = useMemo(() => {
    if (!plans || plans.length === 0) return null;
    return (
      plans.reduce<MembershipPlan | null>(
        (best, p) => ((p.monthlyPriceCents ?? 0) > (best?.monthlyPriceCents ?? -1) ? p : best),
        null,
      )?.slug ?? null
    );
  }, [plans]);

  function variantFor(plan: MembershipPlan): PlanVariant {
    if ((plan.monthlyPriceCents ?? 0) === 0) return 'free';
    if (plan.slug === flagshipSlug) return 'flagship';
    return 'blue';
  }

  return (
    <div>
      <MembershipsHero heroImage={heroBackdrop} />

      <section className="container-fc py-8 md:py-12">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <Card key={i} variant="outlined" padded>
                <div className="h-5 w-24 rounded bg-neutral-100" />
                <div className="mt-4 h-7 w-32 rounded bg-neutral-100" />
                <div className="mt-4 h-4 w-full rounded bg-neutral-100" />
                <div className="mt-6 h-9 w-full rounded bg-neutral-100" />
              </Card>
            ))}
          </div>
        ) : isError ? (
          <Card variant="outlined" padded>
            <p className="text-fc-muted" role="alert">
              No pudimos cargar los planes en este momento. Probá recargar la página en unos
              minutos.
            </p>
          </Card>
        ) : !plans || plans.length === 0 ? (
          <Card variant="outlined" padded>
            <p className="text-fc-muted">
              Por ahora no hay planes de membresía disponibles. Volvé más tarde.
            </p>
          </Card>
        ) : (
          <>
            {/* ROUND17 #4: toggle global cycle (mensual ↔ anual). */}
            <CycleToggle value={cycle} onChange={setCycle} />

            <div className="mt-6 grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((plan) => (
                <PlanCard key={plan.slug} plan={plan} cycle={cycle} variant={variantFor(plan)} />
              ))}
            </div>

            <section className="mt-16">
              <div className="flex items-center gap-3">
                <span aria-hidden className="h-6 w-1.5 flex-none rounded-full bg-primary" />
                <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
                  Comparación de planes
                </h2>
              </div>
              <p className="mt-1.5 pl-[18px] text-sm text-fc-muted">
                Precios, puntos y beneficios de cada plan, lado a lado.
              </p>
              <PlanComparison plans={plans} flagshipSlug={flagshipSlug} />
            </section>
          </>
        )}
      </section>
    </div>
  );
}

function CheckIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CycleToggle({
  value,
  onChange,
}: {
  value: Cycle;
  onChange: (c: Cycle) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div
        role="tablist"
        aria-label="Ciclo de facturación"
        className="inline-grid grid-cols-2 gap-1 rounded-card border border-fc-line bg-fc-bg/40 p-1"
      >
        <CycleTab active={value === 'monthly'} onClick={() => onChange('monthly')}>
          Mensual
        </CycleTab>
        <CycleTab active={value === 'annual'} onClick={() => onChange('annual')}>
          Anual
        </CycleTab>
      </div>
      {value === 'annual' && (
        <span className="inline-flex items-center rounded-full bg-success/15 px-3 py-1 text-xs font-medium text-success">
          Ahorrás 2 meses · 10 meses por el precio de 12
        </span>
      )}
    </div>
  );
}

function CycleTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'rounded-card px-4 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        active
          ? 'bg-primary text-fc-bg shadow-sm'
          : 'text-fc-muted hover:bg-neutral-100 hover:text-fc-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function priceParts(plan: MembershipPlan, cycle: Cycle): { amount: string; suffix: string | null } {
  const cents =
    cycle === 'annual' && typeof plan.annualPriceCents === 'number'
      ? plan.annualPriceCents
      : plan.monthlyPriceCents;
  if (cents === null) return { amount: 'A medida', suffix: null };
  if (cents === 0) return { amount: 'Gratis', suffix: null };
  return { amount: formatCOP(cents), suffix: cycle === 'annual' ? '/ año' : '/ mes' };
}

function PlanCard({
  plan,
  cycle,
  variant,
}: {
  plan: MembershipPlan;
  cycle: Cycle;
  variant: PlanVariant;
}): JSX.Element {
  const hasAnnual = typeof plan.annualPriceCents === 'number' && plan.annualPriceCents > 0;
  const effectiveCycle: Cycle = cycle === 'annual' && hasAnnual ? 'annual' : 'monthly';
  const points = effectiveCycle === 'annual' ? plan.pointsAnnual : plan.pointsMonthly;
  const price = priceParts(plan, effectiveCycle);
  // Beneficios REALES del plan (`plan.features`). Se muestran con check; si el
  // dato viene vacío, la sección no se renderiza (no se inventan beneficios).
  const features = plan.features.slice(0, 6);

  const isFlagship = variant === 'flagship';
  const isBlue = variant === 'blue';

  const surface = isFlagship
    ? 'border-primary-700 bg-gradient-to-br from-neutral-900 to-primary-900 text-white shadow-lg'
    : isBlue
      ? 'border-primary/40 bg-fc-paper shadow-md ring-1 ring-primary/15'
      : 'border-fc-line bg-fc-paper shadow-sm';

  const mutedText = isFlagship ? 'text-white/70' : 'text-fc-muted';
  const inkText = isFlagship ? 'text-white' : 'text-fc-ink';
  const divider = isFlagship ? 'border-white/15' : 'border-fc-line/70';
  const checkWrap = isFlagship
    ? 'bg-cyan/20 text-cyan'
    : isBlue
      ? 'bg-primary/10 text-primary'
      : 'bg-success/15 text-success';

  return (
    <div
      className={`relative flex h-full flex-col overflow-hidden rounded-card border p-5 transition duration-200 hover:-translate-y-1 hover:shadow-xl md:p-6 ${surface}`}
    >
      {/* Franja superior de acento por identidad de plan. */}
      <span
        aria-hidden
        className={`absolute inset-x-0 top-0 h-1 ${
          isFlagship ? 'bg-cyan' : isBlue ? 'bg-primary' : 'bg-neutral-300'
        }`}
      />

      <div className="flex items-center justify-between gap-2">
        {isFlagship ? (
          <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium text-white ring-1 ring-inset ring-white/20">
            {plan.name}
          </span>
        ) : (
          <Badge tone={plan.tone}>{plan.name}</Badge>
        )}
      </div>

      <div className="mt-4 flex items-baseline gap-1.5">
        <span className={`font-display text-3xl font-bold tracking-tight ${inkText}`}>
          {price.amount}
        </span>
        {price.suffix && <span className={`text-sm ${mutedText}`}>{price.suffix}</span>}
      </div>
      {hasAnnual && effectiveCycle === 'monthly' && (
        <p className={`mt-1 text-xs ${mutedText}`}>o {formatCOP(plan.annualPriceCents)} al año</p>
      )}
      {hasAnnual && effectiveCycle === 'annual' && (
        <p className={`mt-1 text-xs ${mutedText}`}>
          ≈ {formatCOP(Math.round((plan.annualPriceCents ?? 0) / 12))} / mes
        </p>
      )}

      <p className={`mt-4 text-sm ${mutedText}`}>{plan.tagline}</p>

      {points > 0 && (
        <div
          className={`mt-4 inline-flex items-center gap-2 self-start rounded-card px-3 py-1.5 text-sm ${
            isFlagship ? 'bg-white/10 text-white' : 'bg-primary-50 text-primary-700'
          }`}
        >
          <span className="font-display font-semibold">+{points.toLocaleString('es-CO')}</span>
          <span className={isFlagship ? 'text-white/80' : 'text-primary-700/80'}>
            puntos al suscribirte
          </span>
        </div>
      )}

      {features.length > 0 && (
        <>
          <div className={`mt-5 border-t ${divider}`} />
          <ul className="mt-4 flex flex-col gap-2.5 text-sm">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className={`mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full ${checkWrap}`}
                >
                  <CheckIcon />
                </span>
                <span className={inkText}>{f}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Botón anclado al fondo → alineado entre las 3 cards. Lleva al detalle
          con el ciclo seleccionado en la URL para que MembershipTier abra
          con el toggle correcto (ROUND17 #4 + #10 plan_slug). */}
      <div className="mt-auto pt-6">
        {isFlagship ? (
          <Link
            to={`/memberships/${plan.slug}?cycle=${effectiveCycle}`}
            className="inline-flex h-11 w-full items-center justify-center rounded-card bg-white px-4 text-sm font-semibold text-primary-700 transition-colors hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Ver detalle
          </Link>
        ) : (
          <Button
            as={Link}
            to={`/memberships/${plan.slug}?cycle=${effectiveCycle}`}
            variant={isBlue ? 'primary' : 'secondary'}
            size="md"
            className="w-full justify-center"
          >
            Ver detalle
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Filas de comparación de características. Cada fila escanea los
 * `features` + `benefits` reales del plan buscando una palabra clave; la
 * celda muestra el texto del beneficio que matchea, o "—" si el plan no
 * lo incluye. Así la tabla refleja lo que admin configuró en cada plan.
 */
const COMPARISON_FEATURES: ReadonlyArray<{ label: string; keywords: string[] }> = [
  {
    label: 'Acceso a eventos',
    keywords: [
      'acceso a eventos',
      'acceso vip',
      'eventos vip',
      'eventos básicos',
      'eventos privados',
    ],
  },
  { label: 'Descuento en partners', keywords: ['descuento'] },
  { label: 'Soporte', keywords: ['soporte'] },
  { label: 'Preventas', keywords: ['preventa'] },
  { label: 'Concierge / asistente', keywords: ['concierge', 'asistente'] },
  {
    label: 'Beneficios VIP',
    keywords: ['mesa vip', 'skip the line', 'valet', 'zona vip', 'lounge', 'upgrade', 'invitados'],
  },
];

function findFeature(plan: MembershipPlan, keywords: string[]): string | null {
  const haystack = [...plan.features, ...plan.benefits];
  for (const item of haystack) {
    const lower = item.toLowerCase();
    if (keywords.some((k) => lower.includes(k))) return item;
  }
  return null;
}

function PlanComparison({
  plans,
  flagshipSlug,
}: {
  plans: ReadonlyArray<MembershipPlan>;
  flagshipSlug: string | null;
}): JSX.Element {
  const colClass = (slug: string): string =>
    slug === flagshipSlug ? 'bg-primary-50/60 font-medium text-fc-ink' : 'text-fc-ink';

  return (
    <div className="mt-6 overflow-x-auto rounded-card border border-fc-line shadow-sm">
      <table className="min-w-full divide-y divide-fc-line text-sm">
        <thead className="bg-neutral-50 text-left">
          <tr>
            <th scope="col" className="px-4 py-3.5 font-medium text-fc-muted">
              Característica
            </th>
            {plans.map((p) => (
              <th
                key={p.slug}
                scope="col"
                className={`px-4 py-3.5 font-semibold ${p.slug === flagshipSlug ? 'text-primary-700' : 'text-fc-ink'}`}
              >
                {p.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-fc-line align-top">
          <tr>
            <td className="px-4 py-3 text-fc-muted">Precio mensual</td>
            {plans.map((p) => (
              <td
                key={p.slug}
                className={`px-4 py-3 font-semibold tabular-nums ${colClass(p.slug)}`}
              >
                {p.monthlyPriceCents === null
                  ? 'A medida'
                  : p.monthlyPriceCents === 0
                    ? 'Gratis'
                    : formatCOP(p.monthlyPriceCents)}
              </td>
            ))}
          </tr>
          <tr>
            <td className="px-4 py-3 text-fc-muted">Precio anual</td>
            {plans.map((p) => (
              <td key={p.slug} className={`px-4 py-3 tabular-nums ${colClass(p.slug)}`}>
                {typeof p.annualPriceCents === 'number' && p.annualPriceCents > 0
                  ? formatCOP(p.annualPriceCents)
                  : '—'}
              </td>
            ))}
          </tr>
          <tr className="bg-neutral-50/60">
            <td className="px-4 py-3 text-fc-muted">Puntos al suscribirse</td>
            {plans.map((p) => (
              <td key={p.slug} className={`px-4 py-3 tabular-nums ${colClass(p.slug)}`}>
                {p.pointsMonthly > 0
                  ? `${p.pointsMonthly.toLocaleString('es-CO')} pts/mes · ${p.pointsAnnual.toLocaleString('es-CO')} pts/año`
                  : '—'}
              </td>
            ))}
          </tr>
          {COMPARISON_FEATURES.map((row, idx) => (
            <tr key={row.label} className={idx % 2 === 1 ? 'bg-neutral-50/60' : undefined}>
              <td className="px-4 py-3 text-fc-muted">{row.label}</td>
              {plans.map((p) => {
                const match = findFeature(p, row.keywords);
                return (
                  <td key={p.slug} className={`px-4 py-3 ${colClass(p.slug)}`}>
                    {match ? (
                      <span className="inline-flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-success/15 text-success">
                          <CheckIcon className="h-3 w-3" />
                        </span>
                        <span>{match}</span>
                      </span>
                    ) : (
                      <span aria-label="No incluido" className="text-fc-muted">
                        —
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
